import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, User, LogOut, LayoutDashboard, PenSquare } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ThemeProvider";

const NAV = [
  { to: "/aprender-aleman", label: "Aprender alemán" },
  { to: "/aleman-para-trabajar", label: "Alemán para trabajar" },
  { to: "/vivir-en-suiza", label: "Vivir en Suiza" },
  { to: "/herramientas", label: "Herramientas" },
  { to: "/guias", label: "Guías" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();
  const isEditor = user && ["editor", "reviewer", "admin"].includes(user.role);
  const isAdmin = user && user.role === "admin";

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" aria-label="Principal">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors hover:bg-secondary ${loc.pathname === n.to ? "text-primary" : "text-foreground/80"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={toggle} aria-label="Cambiar tema" data-testid="theme-toggle"
              className="p-2 rounded-md hover:bg-secondary transition-colors">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                {isAdmin && (
                  <Link to="/admin" data-testid="nav-admin">
                    <Button variant="ghost" size="sm">Admin</Button>
                  </Link>
                )}
                {isEditor && (
                  <Link to="/editorial" data-testid="nav-editorial">
                    <Button variant="ghost" size="sm"><PenSquare size={16} className="mr-1" />Editorial</Button>
                  </Link>
                )}
                <Link to="/panel" data-testid="nav-panel">
                  <Button variant="ghost" size="sm"><LayoutDashboard size={16} className="mr-1" />Mi panel</Button>
                </Link>
                <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn">
                  <LogOut size={16} />
                </Button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/iniciar-sesion" data-testid="nav-login">
                  <Button variant="ghost" size="sm">Iniciar sesión</Button>
                </Link>
                <Link to="/registro" data-testid="nav-register">
                  <Button size="sm" className="rounded-full">Empieza gratis</Button>
                </Link>
              </div>
            )}
            <button className="lg:hidden p-2 rounded-md hover:bg-secondary" onClick={() => setOpen(!open)}
              aria-label="Menú" data-testid="mobile-menu-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-md text-sm font-medium hover:bg-secondary">{n.label}</Link>
          ))}
          <div className="pt-3 mt-3 border-t border-border flex flex-col gap-2">
            {user ? (
              <>
                {isAdmin && <Link to="/admin" onClick={() => setOpen(false)}><Button variant="outline" className="w-full">Admin</Button></Link>}
                {isEditor && <Link to="/editorial" onClick={() => setOpen(false)}><Button variant="outline" className="w-full">Editorial</Button></Link>}
                <Link to="/panel" onClick={() => setOpen(false)}><Button variant="outline" className="w-full">Mi panel</Button></Link>
                <Button onClick={() => { setOpen(false); doLogout(); }} className="w-full">Cerrar sesión</Button>
              </>
            ) : (
              <>
                <Link to="/iniciar-sesion" onClick={() => setOpen(false)}><Button variant="outline" className="w-full">Iniciar sesión</Button></Link>
                <Link to="/registro" onClick={() => setOpen(false)}><Button className="w-full">Empieza gratis</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
